/* eslint-disable no-unused-vars */
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import UpdateIcon from "@mui/icons-material/Update";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Breadcrumbs,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Pagination,
  PaginationItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Toast from "../../component/ToastService";
import { StatusType } from "../../constants/Status";
import { formattedDate, formattedDate1 } from "../../service/Configuration";
import {
  deleteTopic,
  topicByID, studyTargetList
} from "./services/AppService";
const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

function Target() {
  const navigate = useNavigate();
  const pageTitle = "Daily Study Targets";
  const [searchQuery, setSearchQuery] = useState("");
  const [topics, setTopics] = useState([]);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const handleClickOpenDeleteDialog = (id) => {
    setSelectedTopicId(id);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedTopicId(null);
  };

  const deleteTopicCall = async () => {
    try {
      const response = await deleteTopic(selectedTopicId);
      if (response.ok) {
        const responseData = await response.json();
        if (responseData?.status === "OK") {
          Toast(StatusType.SUCCESS, "Topic deleted successfully.");
          handleCloseDeleteDialog();
          loadData();
          return responseData;
        } else {
          Toast(
            StatusType.ERROR,
            responseData?.message || "Error deleting topic.",
          );
        }
      } else {
        await handleErrorResponse(response);
      }
    } catch (error) {
      console.error("Error deleting topic:", error);
      Toast(StatusType.ERROR, "An error occurred while deleting the topic.");
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (page = 1, pageSize = 10) => {
    try {
      setTopics([]);
      const response = await studyTargetList({
        search_text: searchQuery ? searchQuery : "",
        page_no: page,
        page_size: pageSize,
      });

      if (response.ok) {
        const responseData = await response.json();
        if (responseData?.status === "OK") {
          setTopics(responseData.data || []);
          setPage(responseData.pageNo || 0);
          setRowsPerPage(responseData.pageSize || 10);
          setTotalPages(responseData.totalPages || 1);
        } else {
          Toast(
            StatusType.ERROR,
            responseData?.message || "Error fetching topics.",
          );
        }
      } else {
        handleErrorResponse(response);
      }
    } catch (error) {
      console.error("Error fetching topics:", error);
      Toast(StatusType.ERROR, "An error occurred while fetching topics.");
    }
  };

  const handleErrorResponse = async (response) => {
    if (response.status === 401) {
      navigate("/auth/logout");
    } else {
      const responseData = await response.json().catch(() => null);
      Toast(
        StatusType.ERROR,
        responseData?.message || "An unexpected error occurred.",
      );
    }
  };
  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      loadData();
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    loadData(newPage, rowsPerPage);
  };

  const addTarget = () => {
    navigate("/app/add-target");
  };
  
  const handleEdit = (id) => {
    navigate(`/app/edit-topic/${id}`);
  };

  const handleRecall = (id) => {
    navigate(`/app/recall/${id}`);
  };

  const handleView = async (id) => {
    try {
      const response = await topicByID(id);
      if (response.ok) {
        const topicDetails = await response.json();
        setSelectedTopic(topicDetails.data); // Assuming the response has a data field containing topic details
        setViewOpen(true);
      } else {
        handleErrorResponse(response);
      }
    } catch (error) {
      console.error("Error fetching topic details:", error);
      Toast(
        StatusType.ERROR,
        "An error occurred while fetching topic details.",
      );
    }
  };

  const handleCloseView = () => {
    setViewOpen(false);
    setSelectedTopic(null);
  };

  return (
    <Container maxWidth="xl" sx={{ p: 2 }}>
      <div className="d-flex justify-content-between">
        <Typography variant="h4" gutterBottom>
          {pageTitle}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Note:{" "}
          <span className="fst-italic">
          Track your study goals and view today's targets in one place.
         </span>
        </Typography>
        <Breadcrumbs aria-label="breadcrumb">
          <Link color="inherit" href="/">
            Home
          </Link>
          <Typography color="textPrimary">{pageTitle}</Typography>
        </Breadcrumbs>
      </div>
      <Container maxWidth="lg px-0">
        <Card variant="outlined">
          <CardContent>
            <Box display="flex" justifyContent="space-between" mb={3}>
              <Box display="flex" alignItems="center">
                <TextField
                  label="Search "
                  variant="outlined"
                  value={searchQuery}
                  size="small"
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyUp={handleKeyPress}
                  className="me-2"
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => loadData(1)}
                >
                  <SearchIcon />
                </Button>
              </Box>
              <Button
                variant="contained"
                color="primary"
                className="float-end"
                onClick={addTarget}
              >
                Add Today's Target
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell sx={{ backgroundColor: "#f5f5f5" }}>
                     ID
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#f5f5f5" }}>
                      Title
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#f5f5f5" }}>
                      Description
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#f5f5f5" }}>
                      Study Durarion
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#f5f5f5" }}>
                      Pomodoro Count
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#f5f5f5" }}>
                      Added Date
                    </TableCell>
                    {/* <TableCell sx={{ backgroundColor: "#f5f5f5" }}>
                      Recall
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ backgroundColor: "#f5f5f5", width: "200px" }}
                    >
                      Action
                    </TableCell> */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topics.length > 0 ? (
                    topics.map((row, index) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.title}</TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 200, // Set a max width for the cell
                            overflow: "hidden", // Hide overflowed text
                            textOverflow: "ellipsis", // Add ellipsis
                            whiteSpace: "nowrap", // Prevent text wrapping
                          }}
                        >
                          {row.description}
                        </TableCell>
                        <TableCell>{row.targetDurationMinutes}</TableCell> 
                        <TableCell>{row.targetDurationMinutes}</TableCell>
                        <TableCell>
                          <span
                            dangerouslySetInnerHTML={{
                              __html: formattedDate(row.modifyDate),
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Update" arrow placement="top">
                            <Button
                              color="success"
                              onClick={() => handleRecall(row.id)}
                            >
                              <UpdateIcon />
                            </Button>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <ButtonGroup
                            variant="outlined"
                            aria-label="Basic button group"
                          >
                            <Tooltip title="View" arrow placement="top">
                              <Button onClick={() => handleView(row.id)}>
                                <VisibilityIcon />
                              </Button>
                            </Tooltip>
                            <Tooltip title="Edit" arrow placement="top">
                              <Button
                                color="success"
                                onClick={() => handleEdit(row.id)}
                              >
                                <EditIcon />
                              </Button>
                            </Tooltip>
                            <Tooltip title="Delete" arrow placement="top">
                              <Button
                                color="error"
                                onClick={() =>
                                  handleClickOpenDeleteDialog(row.id)
                                }
                              >
                                <DeleteIcon />
                              </Button>
                            </Tooltip>
                          </ButtonGroup>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow sx={{ height: "150px" }}>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body1" color="textSecondary">
                          No records found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box mt={2} display="flex" justifyContent="flex-end">
              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page + 1} // Pagination component uses 1-based index
                  shape="rounded"
                  variant="outlined"
                  color="primary"
                  onChange={handleChangePage} // Handle page changes
                  disabled={totalPages <= 1} // Disable pagination if there's only one page
                  renderItem={(item) => (
                    <PaginationItem
                      {...item}
                      disabled={
                        (item.type === "next" && page === totalPages - 1) ||
                        (item.type === "previous" && page === 0)
                      }
                    />
                  )}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      </Container>
      <BootstrapDialog
        onClose={handleCloseView}
        aria-labelledby="customized-dialog-title"
        open={viewOpen}
        maxWidth="md"
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          Topic Details
          <IconButton
            aria-label="close"
            onClick={handleCloseView}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ width: "700px" }}>
          {selectedTopic && (
            <Box>
              <Box mb={2} display="flex" alignItems="center">
                <Typography
                  component="div"
                  sx={{ marginRight: "10px", fontWeight: "bold" }}
                >
                  Topic Name:
                </Typography>
                <Typography>{selectedTopic.topicName}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box mb={2} display="flex" alignItems="center">
                <Typography
                  component="div"
                  sx={{ marginRight: "10px", fontWeight: "bold" }}
                >
                  Description:
                </Typography>
                <Typography>{selectedTopic.description}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box mb={2} display="flex" alignItems="center">
                <Typography
                  component="div"
                  sx={{ marginRight: "10px", fontWeight: "bold" }}
                >
                  Subject:
                </Typography>
                <Typography>{selectedTopic.subjectName}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box mb={2} display="flex" alignItems="center">
                <Typography
                  component="div"
                  sx={{ marginRight: "10px", fontWeight: "bold" }}
                >
                  Study Material:
                </Typography>
                <Typography>{selectedTopic.studyMaterial}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box mb={2} display="flex" alignItems="center">
                <Typography
                  component="div"
                  sx={{ marginRight: "10px", fontWeight: "bold" }}
                >
                  Exam Name:
                </Typography>
                <Typography>{selectedTopic.examName}</Typography>
              </Box>

              <Divider sx={{ my: 1 }} />
              <Box mb={2} display="flex" alignItems="center">
                <Typography
                  component="div"
                  sx={{ marginRight: "10px", fontWeight: "bold" }}
                >
                  Retention Percentage:
                </Typography>
                <Typography>{selectedTopic.retentionPercentage}%</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box mb={2} display="flex" alignItems="center">
                <Typography
                  component="div"
                  sx={{ marginRight: "10px", fontWeight: "bold" }}
                >
                  Number of Recall:
                </Typography>
                <Typography>{selectedTopic.noOfRevision}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box mb={2} display="flex" alignItems="center">
                <Typography
                  component="div"
                  sx={{ marginRight: "10px", fontWeight: "bold" }}
                >
                  Last Recall Date:
                </Typography>
                <Typography>
                  {formattedDate1(selectedTopic.lastReviewDate)}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box mb={2} display="flex" alignItems="center">
                <Typography
                  component="div"
                  sx={{ marginRight: "10px", fontWeight: "bold" }}
                >
                  Last Recall Type:
                </Typography>
                <Typography>{selectedTopic.lastRecallType}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box mb={2} display="flex" alignItems="center">
                <Typography
                  component="div"
                  sx={{ marginRight: "10px", fontWeight: "bold" }}
                >
                  Added Date:
                </Typography>
                <Typography>
                  {formattedDate1(selectedTopic.addedDate)}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box mb={2} display="flex" alignItems="center">
                <Typography
                  component="div"
                  sx={{ marginRight: "10px", fontWeight: "bold" }}
                >
                  Modify Date:
                </Typography>
                <Typography>
                  {formattedDate1(selectedTopic.modifyDate)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseView} color="primary">
            Close
          </Button>
        </DialogActions>
      </BootstrapDialog>

      <BootstrapDialog
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-confirmation-dialog-title"
        open={deleteDialogOpen}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="delete-confirmation-dialog-title">
          Confirm Deletion
          <IconButton
            aria-label="close"
            onClick={handleCloseDeleteDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete this topic? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={deleteTopicCall} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </Container>
  );
}

export default Target;
